import { useCallback, useEffect, useState } from 'react'
import {
  clearDeferredInstallEvent,
  getDeferredInstallEvent,
  subscribeToInstallPrompt,
  type BeforeInstallPromptEvent,
} from './beforeInstallPrompt'
import {
  detectInstallFormFactor,
  detectInstallPlatform,
  isAppInstalled,
  type InstallFormFactor,
  type InstallPlatform,
} from './installPlatform'
import {
  selectHasDismissedInstallPrompt,
  useInstallPromptStore,
} from '@/application/stores/install-prompt.store'

export interface InstallPromptState {
  /** True when the invitation should be rendered at all. */
  isVisible: boolean
  platform: InstallPlatform
  formFactor: InstallFormFactor
  /** True when the browser offered a one-tap install (Chromium only). */
  canPromptNatively: boolean
  /** Runs the native install flow; a no-op when the browser offered none. */
  promptInstall: () => Promise<void>
  dismiss: () => void
}

/**
 * Drives the "add to home screen" invitation: which platform's instructions
 * to show, where to anchor them, and whether a native one-tap install is
 * available instead.
 *
 * The invitation is withheld when the app is already installed, when the
 * visitor has closed it before, and on desktop — "home screen" means nothing
 * there, and the browsers that do support desktop installs surface their own
 * omnibox affordance for it.
 */
export function useInstallPrompt(): InstallPromptState {
  // Read once: neither the OS nor the form factor changes within a session,
  // and re-reading on every render would just re-run the UA parsing.
  const [platform] = useState<InstallPlatform>(() => detectInstallPlatform())
  const [formFactor] = useState<InstallFormFactor>(() => detectInstallFormFactor())

  const [installed, setInstalled] = useState<boolean>(() => isAppInstalled())
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(() =>
    getDeferredInstallEvent(),
  )

  const hasDismissed = useInstallPromptStore(selectHasDismissedInstallPrompt)
  const dismiss = useInstallPromptStore((state) => state.dismiss)

  useEffect(() => {
    // The event usually arrives before this component mounts, which is why
    // the buffer exists — but it can also arrive later (Chromium re-fires it
    // after the service worker settles), so stay subscribed.
    const unsubscribe = subscribeToInstallPrompt(setDeferredEvent)
    const onAppInstalled = () => {
      setInstalled(true)
      dismiss()
    }

    window.addEventListener('appinstalled', onAppInstalled)
    return () => {
      unsubscribe()
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [dismiss])

  const promptInstall = useCallback(async () => {
    if (deferredEvent === null) return

    await deferredEvent.prompt()
    const { outcome } = await deferredEvent.userChoice

    // Single-use either way: Chromium refuses a second `prompt()` on the same
    // event. A refusal here is still an answer, so the invitation goes away.
    clearDeferredInstallEvent()
    setDeferredEvent(null)
    if (outcome === 'accepted') setInstalled(true)
    dismiss()
  }, [deferredEvent, dismiss])

  const isVisible = !installed && !hasDismissed && platform !== 'other' && formFactor !== 'desktop'

  return {
    isVisible,
    platform,
    formFactor,
    canPromptNatively: deferredEvent !== null,
    promptInstall,
    dismiss,
  }
}
